// ---------------------------------------------------------------------------
// Single source of truth for every PepDirect Learn/research-guide page.
//
// The /learn hub, the /peptides Research Guides pill row, the MobileDrawer
// LEARN group, footer link, and future comparison/methodology guides all read
// from this file. To add a new Learn page:
//   1. Publish its route in App.js
//   2. Add its metadata entry here with `published: true`
//   3. Add its URL to /app/frontend/public/sitemap.xml
// Do NOT maintain a second parallel list in another file.
//
// Fields:
//   slug        canonical slug appended to /learn/
//   title       card title / navigation label (question form, matches page H1)
//   description 1–2 sentence description used on cards
//   topic       short topic label ("Compound", "Metabolic", "Blends", …)
//   category    one of: "compound" | "metabolic" | "blends" | "comparison" |
//               "methodology", used by /learn hub to group cards
//   featured    surfaced in the "Featured Guides" row on /learn
//   drawer      exposed in the MobileDrawer LEARN group (kept short as the
//               library grows, most guides remain discoverable through /learn)
//   published   guides with published:false are hidden from every public
//               surface (hub, /peptides pills, drawer, footer)
//   sort        integer priority (lower = earlier). Category buckets and the
//               "All guides" grid respect this ordering.
// ---------------------------------------------------------------------------
export const LEARN_GUIDES = [
  {
    slug: "retatrutide",
    title: "What Is Retatrutide?",
    description:
      "An overview of retatrutide, triple-receptor agonism and the current research landscape.",
    topic: "Metabolic",
    category: "metabolic",
    featured: true,
    drawer: true,
    published: true,
    sort: 10,
  },
  {
    slug: "nad-plus",
    title: "What Is NAD+?",
    description:
      "Learn about NAD+, cellular redox biology and its relationship to NADH, NR and NMN.",
    topic: "Metabolic",
    category: "metabolic",
    featured: true,
    drawer: true,
    published: true,
    sort: 20,
  },
  {
    slug: "peptide-blends",
    title: "What Are Peptide Blends?",
    description:
      "Understand multi-component peptide formulations, chemical identity and the difference between ingredient-level and blend-level evidence.",
    topic: "Blends",
    category: "blends",
    featured: true,
    drawer: true,
    published: true,
    sort: 30,
  },
  {
    slug: "ghk-cu",
    title: "What Is GHK-Cu?",
    description:
      "The copper-bound tripeptide, GHK vs GHK-Cu identity, copper coordination and the areas studied in laboratory research.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 40,
  },
  {
    slug: "bpc-157",
    title: "What Is BPC-157?",
    description:
      "The pentadecapeptide, research themes, evidence limitations and its relationship to TB-500 and PepDirect blends.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 50,
  },
  {
    slug: "semax",
    title: "What Is Semax?",
    description:
      "The ACTH(4-7)-derived heptapeptide, sequence, neurobiology research themes and evidence limitations.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 60,
  },
  {
    slug: "kpv",
    title: "What Is KPV?",
    description:
      "The Lys-Pro-Val tripeptide, alpha-MSH relationship, signalling pathways studied and its role in the KLOW blend.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 70,
  },
  {
    slug: "semax-vs-selank",
    title: "Semax vs Selank",
    description:
      "Side-by-side comparison of Semax and Selank, sequences, research histories and how the evidence for each should be interpreted.",
    topic: "Comparison",
    category: "comparison",
    featured: false,
    drawer: false,
    published: true,
    sort: 80,
  },
  {
    slug: "cjc-1295-dac-vs-no-dac",
    title: "CJC-1295 With DAC vs Without DAC",
    description:
      "Structure, terminology and research differences between CJC-1295 with DAC and the peptide commonly labelled 'CJC-1295 No DAC' (Modified GRF 1-29).",
    topic: "Comparison",
    category: "comparison",
    featured: false,
    drawer: false,
    published: true,
    sort: 90,
  },
  {
    slug: "bpc-157-vs-tb-500",
    title: "BPC-157 vs TB-500: Research Comparison",
    description:
      "Chemical identity, sequence and research differences between BPC-157 (a 15-amino-acid pentadecapeptide) and TB-500 (the acetylated 7-amino-acid thymosin-beta-4 17–23 fragment).",
    topic: "Comparison",
    category: "comparison",
    featured: false,
    drawer: false,
    published: true,
    sort: 100,
  },
  {
    slug: "glow-vs-klow",
    title: "GLOW vs KLOW Peptide Blends",
    description:
      "Side-by-side composition of the GLOW and KLOW research blends, shared components (GHK-Cu, BPC-157, TB-500) and the addition of KPV in KLOW.",
    topic: "Comparison",
    category: "comparison",
    featured: false,
    drawer: false,
    published: true,
    sort: 110,
  },
  {
    slug: "what-is-tb-500",
    title: "What Is TB-500?",
    description:
      "TB-500 identity as PepDirect supplies it, the acetylated thymosin-beta-4 17–23 fragment, versus full-length thymosin beta-4, plus research context.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 55,
  },
  {
    slug: "what-is-ipamorelin",
    title: "What Is Ipamorelin?",
    description:
      "The selective growth-hormone-secretagogue pentapeptide, receptor pharmacology, research context and its role in the HULK research blend.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 65,
  },
  {
    slug: "how-to-read-peptide-coa",
    title: "How to Read a Peptide Certificate of Analysis",
    description:
      "How a peptide COA is structured, lot matching, HPLC purity, chemical identity, and what a single test result does and does not establish.",
    topic: "Methodology",
    category: "methodology",
    featured: false,
    drawer: false,
    published: true,
    sort: 200,
  },
  {
    slug: "what-is-cagrilintide",
    title: "What Is Cagrilintide?",
    description:
      "The acylated amylin analogue, receptor pharmacology (AMY1R/AMY2R/AMY3R), acylation-based pharmacokinetic design and evidence landscape.",
    topic: "Metabolic",
    category: "metabolic",
    featured: false,
    drawer: false,
    published: true,
    sort: 25,
  },
  {
    slug: "what-is-mots-c",
    title: "What Is MOTS-C?",
    description:
      "The 16-amino-acid mitochondrial-derived peptide encoded within the mitochondrial 12S rRNA, mitonuclear signalling and metabolic-stress research.",
    topic: "Metabolic",
    category: "metabolic",
    featured: false,
    drawer: false,
    published: true,
    sort: 27,
  },
  {
    slug: "what-is-epitalon",
    title: "What Is Epitalon (Epithalon)?",
    description:
      "The AEDG tetrapeptide, identity, both spellings (Epitalon / Epithalon) and the shape of the published telomerase-associated and gene-expression research.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 75,
  },
  {
    slug: "what-is-dsip",
    title: "What Is DSIP?",
    description:
      "Delta Sleep-Inducing Peptide, the nine-amino-acid nonapeptide, its historical sleep-neuroscience context and the limits of the published evidence.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 76,
  },
  {
    slug: "melanotan-1-vs-2",
    title: "Melanotan I vs Melanotan II",
    description:
      "Structure, receptor pharmacology and chemical-identity comparison of Melanotan I (linear α-MSH analogue) and Melanotan II (cyclic melanocortin peptide).",
    topic: "Comparison",
    category: "comparison",
    featured: false,
    drawer: false,
    published: true,
    sort: 115,
  },
  {
    slug: "what-is-aod-9604",
    title: "What Is AOD-9604?",
    description:
      "The hGH C-terminal peptide fragment (residues 176–191), identity, distinction from full-length growth hormone and preclinical lipid-metabolism research.",
    topic: "Metabolic",
    category: "metabolic",
    featured: false,
    drawer: false,
    published: true,
    sort: 28,
  },
  {
    slug: "what-is-pt-141",
    title: "What Is PT-141 (Bremelanotide)?",
    description:
      "The cyclic melanocortin peptide, identity, MC4R-focused receptor research and its relationship to Melanotan II.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 77,
  },
  {
    slug: "what-is-cjc-1295",
    title: "What Is CJC-1295?",
    description:
      "The GHRH-analogue research family, CJC-1295 with DAC, CJC-1295 without DAC (Modified GRF 1-29) and why exact chemical identity matters.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 78,
  },
  {
    slug: "what-is-selank",
    title: "What Is Selank?",
    description:
      "The tuftsin-derived synthetic heptapeptide, identity, PGP-stability extension and the neurochemical / gene-expression research context.",
    topic: "Compound",
    category: "compound",
    featured: false,
    drawer: false,
    published: true,
    sort: 79,
  },
];

export const LEARN_CATEGORIES = [
  {
    key: "compound",
    heading: "Peptides & Compounds",
    anchor: "peptides-compounds",
    intro:
      "Guides to individual research peptides and biochemical compounds, identity, mechanism, and the shape of the published evidence.",
  },
  {
    key: "metabolic",
    heading: "Metabolic Research",
    anchor: "metabolic-research",
    intro:
      "Guides covering compounds and pathways studied in metabolic and endocrine research contexts.",
  },
  {
    key: "blends",
    heading: "Peptide Blends",
    anchor: "peptide-blends",
    intro:
      "Learn how multi-component research formulations are documented and why evidence for individual ingredients does not automatically establish evidence for the exact blend.",
  },
  {
    key: "comparison",
    heading: "Compound Comparisons",
    anchor: "compound-comparisons",
    intro:
      "Structured comparisons between related research peptides, identity, mechanism and evidence differences.",
  },
  {
    key: "methodology",
    heading: "Research Methods & Testing",
    anchor: "research-methods",
    intro:
      "Guides on how to read and interpret peptide research literature, batch-testing paperwork and lot-level documentation.",
  },
];

// Convenience helpers ---------------------------------------------------------
const bySort = (a, b) => a.sort - b.sort;

export function publishedGuides() {
  return LEARN_GUIDES.filter((g) => g.published).sort(bySort);
}

export function featuredGuides() {
  return publishedGuides().filter((g) => g.featured);
}

export function drawerGuides() {
  return publishedGuides().filter((g) => g.drawer);
}

export function guidesByCategory(key) {
  return publishedGuides().filter((g) => g.category === key);
}

export function guideTitle(slug) {
  return publishedGuides().find((g) => g.slug === slug)?.title || null;
}
